import { Component, OnDestroy, ViewChild,
  HostBinding, ElementRef, ViewEncapsulation
} from "@angular/core";
import { NavigationEnd, Router } from "@angular/router";
import { Subscription } from "rxjs";
import { filter } from "rxjs/operators";
import { BreakpointObserver } from "@angular/cdk/layout";
import { MatSidenav, MatSidenavContent } from "@angular/material/sidenav";
import { environment } from "environments/environment";
import { SettingsService } from "@core";
import { SharedService } from "app/services/core/shared.service";
import { AuthService } from "@shared/auth/auth.service";

const MOBILE_MEDIAQUERY = "screen and (max-width: 599px)";
const TABLET_MEDIAQUERY = "screen and (min-width: 600px) and (max-width: 959px)";
const MONITOR_MEDIAQUERY = "screen and (min-width: 960px)";

@Component({
  selector: "app-default-layout",
  templateUrl: "./default-layout.component.html",
  styleUrls: ["./default-layout.component.scss"],
  encapsulation: ViewEncapsulation.None,
})
export class DefaultLayoutComponent implements OnDestroy {
  @ViewChild("sidenav") sidenav: MatSidenav;
  @ViewChild("content", { static: true }) content: MatSidenavContent;

  options = this.settings.getOptions();

  private environment = environment;
  private layoutChangesSubscription: Subscription;
  private themeSubscription: Subscription;
  public build: string = null;
  public production: boolean = true;
  public useTimeout: boolean = false;
  public displaySidebar: boolean = this.environment.displaySidebar;
  private isMobileScreen = false;
  private isContentWidthFixed = true;
  private isCollapsedWidthFixed = false;
  public userName: string = null;

  private collapseForPrinting: Subscription;

  get isMobile(): boolean {
    return this.isMobileScreen;
  }

  @HostBinding("class.matero-content-width-fix") get contentWidthFix() {
    return (
      this.isContentWidthFixed &&
      this.options.navPos === "side" &&
      this.options.sidenavOpened &&
      !this.isMobile
    );
  }

  @HostBinding("class.matero-sidenav-collapsed-fix") get collapsedWidthFix() {
    return (
      this.isCollapsedWidthFixed &&
      (this.options.navPos === "top" ||
        (this.options.sidenavOpened && this.isMobile))
    );
  }

  constructor(
    private router: Router,
    private breakpointObserver: BreakpointObserver,
    private element: ElementRef,
    public sharedService: SharedService,
    public authService: AuthService,
    private settings: SettingsService
  ) {
    this.layoutChangesSubscription = this.breakpointObserver
      .observe([MOBILE_MEDIAQUERY, TABLET_MEDIAQUERY, MONITOR_MEDIAQUERY])
      .subscribe((state) => {
        // SidenavOpened must be reset true when layout changes
        this.options.sidenavOpened = true;
        this.isMobileScreen = state.breakpoints[MOBILE_MEDIAQUERY];
        this.sharedService.mobile = state.breakpoints[MOBILE_MEDIAQUERY];
        this.options.sidenavCollapsed = state.breakpoints[TABLET_MEDIAQUERY];
        this.isContentWidthFixed = state.breakpoints[MONITOR_MEDIAQUERY];
      });

    this.themeSubscription = this.sharedService.themeChanged$.subscribe(
      () => this.setTheme()
    );

    this.authService.currentUserEvent.subscribe(
      () => {this.userName = this.authService.user?.fullName || null  }  
    );

    // TODO: Scroll top to container
    this.router.events
      .pipe(filter((event) => event instanceof NavigationEnd))
      .subscribe((e) => {
        this.content.scrollTo({ top: 0 });
      });

    // Initialize project theme
    this.setTheme();
  }

  ngOnInit() {
    this.useTimeout = environment.useTimeout;
    this.build = environment.build;
    this.production = environment.production;
    if (!this.isMobileScreen && this.environment.collapsibleSidebar) {
      this.toggleCollapsed();
    }
    if (!this.sidenavOpen()) {
      this.sidenavOpenedChange(false);
    }
  }

  sidenavOpen(): boolean {
    return (
      this.options.navPos === "side" &&
      this.options.sidenavOpened &&
      !this.isMobile &&
      environment.displaySidebar
    );
  }

  ngOnDestroy() {
    this.layoutChangesSubscription.unsubscribe();
    this.themeSubscription.unsubscribe();
  }

  toggleCollapsed() {
    this.isContentWidthFixed = false;
    this.options.sidenavCollapsed = !this.options.sidenavCollapsed;
    this.resetCollapsedState();
  }

  // TODO: Trigger when transition end
  resetCollapsedState(timer = 400) {
    setTimeout(() => this.settings.setOptions(this.options), timer);
  }

  sidenavCloseStart() {
    this.isContentWidthFixed = false;
  }

  sidenavOpenedChange(isOpened: boolean) {
    this.isCollapsedWidthFixed = !this.isMobile;
    this.options.sidenavOpened = isOpened;
    this.settings.setOptions(this.options);
  }

  setTheme() {
    if (this.sharedService.userTheme === "dark") {
      this.element.nativeElement.classList.add("theme-dark");
    } else {
      this.element.nativeElement.classList.remove("theme-dark");
    }
  }
}
